<template>
  <div class="m-b-16">
    <div v-if="projectId" class="card">
      <!--------委托项目---------->
      <entrust-info-dialog
        v-model="orderId"
        :read-only="true"
        :is-release="true"
      />
    </div>

    <!---------招标信息-------------->
    <div class="card">
      <div class="section">
        <div class="section-title">
          <base-section-title title="招标信息" />
        </div>
        <div class="section-content">
          <jczh-descriptions
            :info="orderInfo"
            :option="biddingInfoOption"
            :el-attrs="{
              size: 'medium',
              labelStyle: {
                whiteSpace: 'nowrap'
              }
            }"
          >
            <template v-slot:budget="{ value }">
              <span class="m-r-10">
                {{ value ? strToThousands(value) : '-' }} 万元
              </span>
              <money-in-cn :money="value * 10000" />
            </template>
          </jczh-descriptions>
        </div>
      </div>
    </div>

    <!----------招标方式-------------->
    <div class="card">
      <div class="section">
        <div class="section-title">
          <base-section-title title="招标方式" />
        </div>
        <div class="section-content">
          <jczh-descriptions
            key="bidMethod"
            :info="orderInfo"
            :option="biddingMethodOption"
            :el-attrs="{
              size: 'medium',
              labelStyle: {
                whiteSpace: 'nowrap'
              }
            }"
          >
            <template v-slot:otherRequire="{ value }">
              <other-material-requirements :table="value || []" mode="detail" />
            </template>
            <template v-slot:inviteSupNormal>
              <div>
                <el-tag
                  v-for="tag in inviteSupNormal"
                  :key="tag.supplierId"
                  class="m-b-4 m-r-4"
                >
                  {{ tag.supplierName }}
                </el-tag>
              </div>
            </template>
            <template v-slot:inviteSupAdd>
              <div>
                <el-tag
                  v-for="tag in inviteSupAdd"
                  :key="tag.supplierId"
                  class="m-b-4 m-r-4"
                >
                  {{ tag.supplierName }}
                </el-tag>
              </div>
            </template>
            <template v-slot:supplierCategoryList>
              <div v-if="supplierCategoryList.length">
                <select-group-supplier
                  v-model="supplierCategoryList"
                  :normalize-in="normalizeInGroup"
                  :read-only="true"
                />
              </div>
              <span v-else>-</span>
            </template>
            <template v-slot:internalAttachments>
              <upload
                v-if="orderInfo.internalAttachments"
                :file-id-before="orderInfo.internalAttachments"
                :props-data="{
                  disabled: true,
                  uploadMode: 'fileId'
                }"
              />
              <span v-else>-</span>
            </template>
          </jczh-descriptions>
        </div>
      </div>
    </div>

    <!----------招标文件------------>
    <div class="card">
      <div class="section">
        <div class="section-title">
          <base-section-title title="招标文件" />
        </div>
        <div class="section-content">
          <jczh-descriptions
            :info="orderInfo"
            :option="bidFileInfoOption"
            :el-attrs="{
              size: 'medium',
              labelStyle: {
                whiteSpace: 'nowrap'
              }
            }"
          >
            <template v-slot:bookFee="{ value }">
              <span>{{ value ? `${strToThousands(value)} 元` : '-' }}</span>
              <money-in-cn v-if="value" class="m-l-4" :money="value" />
            </template>

            <!-- 平台使用费 -->
            <template v-slot:platformFee="{ value }">
              <div>
                <span class="m-r-10">
                  {{ value ? `${strToThousands(value)} 元` : '-' }}
                </span>
                <el-button
                  class="p-0"
                  type="text"
                  @click="openPlatformFeeDetail"
                >
                  查看计费规则
                </el-button>
              </div>
              <money-in-cn v-if="value" class="m-l-5" :money="value" />
            </template>

            <!-- 平台服务费 -->
            <template v-if="serviceFeeChargeConfig.configId" v-slot:serviceFee>
              <el-button
                v-if="
                  serviceFeeChargeConfig.chargeType ===
                    chargeTypeDict.CHARGE.value
                "
                class="p-t-3"
                type="text"
                @click="openServiceFeeDetail"
              >
                查看计费规则
              </el-button>
              <div
                v-else-if="
                  serviceFeeChargeConfig.chargeType ===
                    chargeTypeDict.FREE.value
                "
              >
                <span>0 元</span>
                <money-in-cn money="0" />
              </div>
              <div v-else>-</div>
            </template>

            <template v-slot:bidFile>
              <el-button class="p-3" type="text" @click="toBidFileDetail">
                查看详情
              </el-button>
            </template>

            <!-- 上传附件 -->
            <template v-slot:biddingAttachmentIds>
              <upload-authorization-file
                ref="UploadAuthorizationFile"
                v-model="biddingAttachmentIds"
                style="width: 260px"
                :preview-api="previewBiddingFileApi"
                :download-api="downloadBiddingFileApi"
                upload-mode="fileId"
                :request-params="{
                  isRelease: true,
                  orderId: orderId,
                  fileType: fileTypeDict.BID.value
                }"
                :props-data="{
                  disabled: true
                }"
              />
            </template>

            <!-- 其他附件 -->
            <template v-slot:otherAttachment>
              <upload-authorization-file
                v-if="otherAttachmentIds"
                ref="UploadAuthorizationFile"
                v-model="otherAttachmentIds"
                style="width: 260px"
                class="upload"
                upload-mode="fileId"
                :preview-api="previewBiddingFileApi"
                :download-api="downloadBiddingFileApi"
                :request-params="{
                  isRelease: true,
                  orderId: orderId,
                  fileType: fileTypeDict.OTHER.value
                }"
                :props-data="{
                  disabled: true
                }"
              />
              <span v-else>-</span>
            </template>
          </jczh-descriptions>
          <!-----暂未上传招标文件------->
          <p v-if="!biddingAttachmentIds" class="no-file-tip">
            暂未上传招标文件
          </p>

          <slot name="statistics"></slot>
        </div>
      </div>
    </div>

    <!------保证金设置---------->
    <div class="card">
      <div class="section">
        <div class="section-title">
          <base-section-title title="保证金设置" />
        </div>
        <div class="section-content">
          <jczh-descriptions
            :info="bondInfo"
            :option="bondInfoOptions"
            :el-attrs="{
              size: 'medium',
              labelStyle: {
                whiteSpace: 'nowrap'
              }
            }"
          >
            <template v-slot:guaranteeFee="{ value }">
              <span>{{ value ? strToThousands(value) : '-' }}</span>
              <money-in-cn :money="value" />
            </template>
          </jczh-descriptions>
        </div>
      </div>
    </div>

    <!----------所有公告-------------->
    <div v-if="isShowNotice" class="card">
      <div class="section">
        <div class="section-title">
          <base-section-title title="所有公告" />
        </div>
        <div class="section-content">
          <bulletin-item
            :bulletin-label="
              orderInfo.releaseMethod === bidPublishWayDict.NOTICE_BIDDING.value
                ? '采购公告'
                : '邀请函'
            "
            :title="orderInfo.title"
            :content="orderInfo.content"
            :release-time="orderInfo.releaseTime"
            :button-text="
              orderInfo.releaseMethod === bidPublishWayDict.NOTICE_BIDDING.value
                ? '查看公告'
                : '查看邀请函'
            "
          />
          <slot name="notice"></slot>
        </div>
      </div>
    </div>

    <!-- 平台使用费计费规则 --------->
    <charge-config-dialog
      ref="chargeConfigDialog"
      :config-obj="platformFeeConfig"
    />

    <!-- 平台服务费计费规则 --------->
    <charge-config-dialog
      v-if="serviceFeeChargeConfig.configId"
      ref="serviceFeeConfigDialog"
      :config-obj="serviceFeeChargeConfig"
    />
  </div>
</template>

<script>
import BaseSectionTitle from '@/components/common/BaseSectionTitle'
import EntrustInfoDialog from '@/components/entrust/EntrustInfoDialog'
import UploadAuthorizationFile from '@/components/common/Upload/UploadAuthorizationFile'
import BulletinItem from '@/components/common/Bulletins/BulletinItem'
import SelectGroupSupplier from '@/components/common/SelectSupplierDrawer/SelectGroupSupplier.vue'
import MoneyInCn from '@/components/common/MoneyInCn'
import OtherMaterialRequirements from '@/components/common/OtherMaterialRequirements/OtherMaterialRequirements'
import ChargeConfigDialog from '@/components/common/ChargeConfigDialog'
import Upload from '@/components/common/Upload/index'

// 数据字典
import {
  bidWinWayDict,
  bidInviteWayDict,
  bidRegisterAuditFlagDict,
  bidPublishWayDict,
  isComboDict,
  needBidBondDict,
  bondBidTypeDict,
  uploadBiddingFileTypeDict,
  bookFeeFreeRangeDict,
  bidChargeStatusDict,
  bidInviteBidStatusDict,
  bookFeeSendDict,
  reissueUploadFileTypeDict,
  fileTypeDict
} from '@/dictionary/project/bid'
import {
  guaranteePayMethodDict,
  guaranteePayMethodSupportDict,
  projectOpenScopeDict,
  projectTypeDict,
  releaseMediaDict,
  offerTypeDict,
  whetherDict,
  isSignupShowListingDict,
  projectAssetDisposalMethodDict
} from '@/dictionary/project/common'
import { chargeTypeDict, costSubjectDict } from '@/dictionary/company'

import {
  fetchInviteBidDetailApi,
  previewBiddingFileApi,
  downloadBiddingFileApi
} from '@/api/procure/invite-bid'
import { getOpenBiddingProjectApi } from '@/api/procure/kpb-project'

import { strToThousands } from '@/utils/utils'
import DateUtils from '@/utils/date'
import { cloneDeep, throttle } from 'lodash'
import { mapState } from 'vuex'
import { workbenchServiceDict } from '@/dictionary/system'

export default {
  components: {
    BaseSectionTitle,
    EntrustInfoDialog,
    UploadAuthorizationFile,
    BulletinItem,
    SelectGroupSupplier,
    MoneyInCn,
    OtherMaterialRequirements,
    ChargeConfigDialog,
    Upload
  },

  props: {
    isByOrderId: {
      type: Boolean,
      default: true
    },

    requestQuery: {
      type: Object,
      default: () => {}
    },

    /**
     * 是否展示公告
     */
    isShowNotice: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      fileTypeDict,
      bookFeeFreeRangeDict,
      bidChargeStatusDict,
      bidInviteBidStatusDict,
      bidPublishWayDict,
      isSignupShowListingDict,
      chargeTypeDict,

      previewBiddingFileApi,
      downloadBiddingFileApi,

      orderId: '',
      orderInfo: {},
      inviteSupAdd: [], // 追加供应商
      inviteSupNormal: [], // 初始邀请的供应商
      bondInfo: {}, // 保证金设置

      platformFeeConfig: {}
    }
  },

  computed: {
    ...mapState('router', ['currentWorkbench']),

    // 是否是代理机构操作
    isAgency() {
      return this.currentWorkbench === workbenchServiceDict.AGENCY.value
    },

    // 路由前缀
    routerPrefix() {
      const firstWord = (
        this.$route.name?.replace(/([A-Z])/g, $0 => `-${$0}`)?.split('-') || []
      ).filter(v => v)[0]

      if (firstWord === 'Bidding') return ''
      else return firstWord || ''
    },

    // 招标文件
    biddingAttachmentIds() {
      return this.orderInfo.biddingAttachmentIds &&
        this.orderInfo.biddingAttachmentIds.length
        ? this.orderInfo.biddingAttachmentIds.join(',')
        : ''
    },

    // 其他文件
    otherAttachmentIds() {
      return this.orderInfo.otherAttachmentIds &&
        this.orderInfo.otherAttachmentIds.length
        ? this.orderInfo.otherAttachmentIds.join(',')
        : ''
    },

    // 委托项目id
    projectId() {
      return this.orderInfo.projectId || 0
    },

    // 招标文件id
    kpbProjectId() {
      return this.orderInfo?.kpbProjectId || 0
    },

    /**
     * 是否展示标书费
     * 非分步上传 || (分步上传 && 开标前上传)
     */
    showBookFee() {
      return (
        this.orderInfo.isReissueFile === uploadBiddingFileTypeDict.YES.value ||
        (this.orderInfo.isReissueFile === uploadBiddingFileTypeDict.NO.value &&
          this.orderInfo.reissueUploadFileType ===
            reissueUploadFileTypeDict.BEFORE_OPEN.value)
      )
    },

    // 是否是公开招标
    inPublicBid() {
      return (
        this.orderInfo.releaseMethod === bidPublishWayDict.NOTICE_BIDDING.value
      )
    },

    // 是否需要审核
    isNeedApproval() {
      return this.orderInfo.isApproval === bidRegisterAuditFlagDict.YES.value
    },

    // 是否是线上开评标项目
    isKpbProject() {
      return this.orderInfo.isKpbProject === whetherDict.YES.value
    },

    // 已选公开范围
    supplierCategoryList() {
      return this.orderInfo.orderSupplierCategoryList || []
    },

    // 招标基础信息
    biddingInfoOption() {
      return [
        {
          label: '招标名称',
          prop: 'orderName',
          elAttrs: { span: 3 }
        },
        {
          label: '招标估算',
          prop: 'budget',
          slotName: 'budget'
        },
        {
          label: '中标方式',
          prop: 'winType',
          filter: value => bidWinWayDict.translate(value).label
        },
        {
          label: '报价方式',
          prop: 'offerType',
          filter: value => offerTypeDict.translate(value).label
        },
        {
          label: '报名时间',
          prop: '',
          filter: value => {
            return DateUtils.getNormMinuteRange(
              value.signUpStartTime,
              value.signUpEndTime
            )
          }
        },
        {
          label: '开标时间',
          prop: 'openTime',
          isNormMinute: true
        },
        {
          label: '项目类型',
          prop: 'biddingType',
          filter: value => projectTypeDict.translate(value).label
        },
        {
          label: '处置方式',
          prop: 'assetDisposalMethod',
          filter: value =>
            projectAssetDisposalMethodDict.translate(value)?.label ||
            projectAssetDisposalMethodDict.EXPENDITURE.label,
          hideFun: () =>
            this.orderInfo.biddingType !== projectTypeDict.ASSET_DISPOSAL.value
        },
        {
          label: '发布媒体',
          prop: 'noticeMedium',
          filter: value => {
            const temp = (value || '').split(',')
            return (
              temp
                .map(mediumValue => {
                  return releaseMediaDict.translate(mediumValue)?.label
                })
                .filter(value => value)
                .join('、') || '-'
            )
          }
        },
        {
          label: '招标地点',
          filter: value => {
            if (value?.country === '中国') {
              return `${value?.province} - ${value?.city} - ${value?.area}`
            } else {
              return `${value?.country} - ${value?.foreignAddress}`
            }
          }
        },
        {
          label: '详细地址',
          prop: 'detailAddress'
        }
      ]
    },

    // 招标方式
    biddingMethodOption() {
      return [
        {
          label: '招标方式',
          prop: 'biddingMethod',
          filter: value =>
            bidInviteWayDict
              .translate(value)
              ?.label?.replace(/招标/, this.bidCopyWriting.name)
        },
        {
          label: '发布方式',
          prop: 'releaseMethod',
          // dictArray: bidPublishWayDict.array
          filter: value =>
            bidPublishWayDict
              .translate(value)
              ?.label?.replace(/招标/, this.bidCopyWriting.name)
        },
        {
          label: '公开范围',
          prop: 'openScope',
          dictArray: projectOpenScopeDict.array,
          hideFun: () => !this.inPublicBid
        },
        {
          label: '已选公开范围',
          slotName: 'supplierCategoryList',
          hideFun: value =>
            !(
              this.inPublicBid &&
              this.orderInfo.openScope === projectOpenScopeDict.COMPANY.value
            ),
          elAttrs: {
            span: 3
          }
        },
        {
          label: '报名时段查看供应商参与名单',
          prop: 'isSignupShowListing',
          dictArray: isSignupShowListingDict.array,
          hideFun: () => !this.inPublicBid
        },
        {
          label: '报名审核',
          prop: 'isApproval',
          dictArray: bidRegisterAuditFlagDict.array,
          hideFun: () => !this.inPublicBid
        },
        {
          label: '联合体报名',
          prop: 'isCombo',
          dictArray: isComboDict.dictArray,
          elAttrs: { span: 2 }
        },
        {
          label: '证件要求',
          prop: 'requireList',
          filter: value => {
            const certNameArray = value.map(item => item.certName)
            return certNameArray.join('，')
          },
          hideFun: () =>
            !this.isNeedApproval || this.orderInfo.requireList.length === 0,
          elAttrs: { span: 3 }
        },
        {
          label: '其他材料要求',
          prop: 'otherMaterialList',
          hideFun: () =>
            !this.isNeedApproval ||
            this.orderInfo.otherMaterialList?.length === 0,
          slotName: 'otherRequire',
          elAttrs: { span: 3 }
        },
        {
          label: '备注',
          prop: 'approvalRemark',
          hideFun: () => !this.isNeedApproval,
          elAttrs: { span: 3 }
        },
        {
          label: '邀请供应商',
          slotName: 'inviteSupNormal',
          hideFun: () => this.inPublicBid,
          elAttrs: { span: 3 }
        },
        {
          label: '追加供应商',
          slotName: 'inviteSupAdd',
          hideFun: () => this.inPublicBid || this.inviteSupAdd.length === 0,
          elAttrs: { span: 3 }
        },
        {
          label: '内部文件',
          prop: 'internalAttachments',
          slotName: 'internalAttachments',
          elAttrs: { span: 3 }
        }
      ]
    },

    // 保证金设置
    bondInfoOptions() {
      return [
        {
          label: '是否收取保证金',
          prop: 'isCollectGuarantee',
          filter: value => needBidBondDict.translate(value).label || '-'
        },
        {
          label: '支持保证金形式',
          prop: '',
          filter: value => {
            const list = []
            // 支持现金
            if (value.supportGf === guaranteePayMethodSupportDict.YES.value) {
              list.push(guaranteePayMethodDict.MONEY_TRANSFER.label)
            }
            // 支持线上保函
            if (
              value.supportOnlineGfLetter ===
              guaranteePayMethodSupportDict.YES.value
            ) {
              list.push(guaranteePayMethodDict.ONLINE_LETTER.label)
            }
            // 支持线下保函
            if (
              value.supportOfflineGfLetter ===
              guaranteePayMethodSupportDict.YES.value
            ) {
              list.push(guaranteePayMethodDict.OFFLINE_LETTER.label)
            }
            return list.join('，')
          },
          hideFun: value =>
            value.isCollectGuarantee === needBidBondDict.NO.value
        },
        {
          label: '现金收取方式',
          prop: 'collectGuaranteeMode',
          filter: value => bondBidTypeDict.translate(value).label || '-',
          hideFun: value =>
            value.isCollectGuarantee === needBidBondDict.NO.value ||
            value.supportGf === guaranteePayMethodSupportDict.NO.value
        },
        {
          label: '保函有效期',
          prop: 'gfLetterDays',
          filter: value => `${value}天`,
          hideFun: value =>
            value.isCollectGuarantee === needBidBondDict.NO.value ||
            value.supportOnlineGfLetter ===
              guaranteePayMethodSupportDict.NO.value ||
            value.supportOfflineGfLetter ===
              guaranteePayMethodSupportDict.NO.value
        },
        {
          label: '保证金金额（元）',
          prop: 'guaranteeFee',
          // filter: value => (value ? strToThousands(value) : '-'),
          slotName: 'guaranteeFee',
          hideFun: value =>
            value.isCollectGuarantee === needBidBondDict.NO.value
        },
        {
          label: '保证金缴纳截止时间',
          prop: 'depositEndTime',
          filter: value => {
            return DateUtils.getNormMinute(value)
          },
          hideFun: value =>
            value.isCollectGuarantee === needBidBondDict.NO.value ||
            value.collectGuaranteeMode !== bondBidTypeDict.PLATFORM.value
        }
      ]
    },

    // 招标文件信息
    bidFileInfoOption() {
      return [
        {
          label: '(流标) 是否免收标书费',
          prop: 'isBookFeeFree',
          filter: value => bidChargeStatusDict.translate(value).label || '-',
          hideFun: () =>
            this.orderInfo.status !== bidInviteBidStatusDict.RESTART.value
        },
        {
          label: '免收范围',
          prop: 'bookFeeFreeRange',
          filter: value => bookFeeFreeRangeDict.translate(value).label || '-',
          hideFun: () =>
            this.orderInfo.isBookFeeFree !== bidChargeStatusDict.YES.value
        },
        {
          label: '电子化开评标',
          prop: 'isKpbProject',
          dictArray: whetherDict.array
        },
        {
          label: '上传招标文件',
          prop: 'isReissueFile',
          dictArray: uploadBiddingFileTypeDict.array,
          hideFun: () => this.isKpbProject
        },
        {
          label: '标书费',
          prop: 'bookFee',
          slotName: 'bookFee',
          hideFun: () => !this.showBookFee
        },
        {
          label: '平台使用费',
          prop: 'platformFee',
          slotName: 'platformFee',
          hideFun: () =>
            !this.showBookFee ||
            this.orderInfo.platformFee === null ||
            this.orderInfo.platformFee === undefined ||
            this.orderInfo.platformFee === ''
        },
        {
          label: '平台服务费',
          slotName: 'serviceFee'
        },
        {
          label: '电子招标文件',
          prop: '',
          slotName: 'bidFile',
          hideFun: () => !this.isKpbProject
        },
        {
          label: '上传文件',
          prop: 'biddingAttachmentIds',
          slotName: 'biddingAttachmentIds',
          hideFun: () => this.isKpbProject || !this.biddingAttachmentIds
        },
        {
          label: this.isKpbProject ? '其它文件(图纸)' : '其他文件',
          prop: 'otherAttachmentIds',
          slotName: 'otherAttachment',
          hideFun: () => !this.orderInfo.otherAttachmentIds?.length
        },
        {
          label: '上传时间',
          prop: 'reissueTime',
          hideFun: () => !this.orderInfo.reissueTime
        }
      ]
    },

    // 平台服务费计费配置
    serviceFeeChargeConfig() {
      return (
        (this.orderInfo?.chargeList || []).find(
          item => Number(item.costSubject) === costSubjectDict.SERVICE_FEE.value
        ) || {}
      )
    },

    // 是否是招商
    isMerchant() {
      return this.orderInfo.isMerchant
    },

    // 招标名称
    bidCopyWriting() {
      if (this.isMerchant) return { name: '招商', fullName: '招商引资' }
      return { name: '招标', fullName: '招标采购' }
    }
  },

  watch: {
    'requestQuery.order': {
      handler: function(val) {
        this.orderId = val?.orderId
        if (val?.orderName) {
          this.orderInfo = cloneDeep(val)
          this.handlePageData()
        }
      },
      immediate: true
    },

    'requestQuery.orderId': {
      handler: function(val) {
        this.orderId = val
        if (this.isByOrderId && val) {
          this.getInviteBidInfo()
        }
      },
      immediate: true
    }
  },

  methods: {
    strToThousands,
    /**
     * 获取招标单详情
     */
    async getInviteBidInfo() {
      try {
        const { data } = await fetchInviteBidDetailApi({
          orderId: this.orderId
        })
        this.orderInfo = data || {}
        this.handlePageData()
      } catch (error) {
        this.$message.error(error?.message)
      }
    },

    /**
     * 处理页面数据
     */
    handlePageData() {
      if (this.isKpbProject) {
        this.getKpbProjectDetail()
      }
      if (this.orderInfo.inviteSup?.length) {
        this.inviteSupNormal = this.orderInfo.inviteSup.filter(
          el => !el.inviteType
        )
        this.inviteSupAdd = this.orderInfo.inviteSup.filter(el => el.inviteType)
      }
      this.bondInfo = this.orderInfo.guarantee || {}
      if (
        Number(this.orderInfo.status) === bidInviteBidStatusDict.RESTART.value
      ) {
        const _value =
          bookFeeSendDict.translate(this.orderInfo.bookFeeFree).alias || ''
        this.orderInfo.isBookFeeFree = Number(_value.split('-')[0])
        this.orderInfo.bookFeeFreeRange = Number(_value.split('-')[1])
      }
      this.platformFeeConfig =
        (this.orderInfo.chargeList || []).find(
          el => el.costSubject == costSubjectDict.REGISTER_FEE.value
        ) || {}
    },

    /**
     * 获取开评标项目详情
     */
    getKpbProjectDetail() {
      const getDetail = () => {
        return getOpenBiddingProjectApi(this.kpbProjectId)
          .then(res => {
            this.kpbProject = res.data || {}
          })
          .catch(err => {
            this.$message?.error(err?.message)
          })
      }
      throttle(getDetail, 100)
    },

    /**
     * 前往查看招标文件详情
     */
    toBidFileDetail() {
      this.$router.push({
        name: this.routerPrefix + 'BiddingFileInfo',
        params: {
          orderId: this.orderId,
          biddingFileId: this.orderInfo.biddingFileId
        }
      })
    },

    /**
     * 打开计费规则
     */
    openPlatformFeeDetail() {
      this.$refs.chargeConfigDialog.open()
    },

    /**
     * 定向公开范围供应商输入字段转换
     */
    normalizeInGroup(list) {
      return list.map(tag => {
        return {
          categoryType: tag.categoryType,
          categoryId: tag.categoryId,
          name: tag.categoryName
        }
      })
    },

    /**
     * 打开服务费规则弹窗
     */
    openServiceFeeDetail() {
      this.$refs.serviceFeeConfigDialog.open()
    }
  }
}
</script>
<style lang="scss" scoped>
.no-file-tip {
  color: $second-text-color;
}
</style>
